import { useCallback, useMemo, useReducer, useRef } from "react";
import { interactionModeReducer, INITIAL_INTERACTION_MODE } from "./reducer";
import type {
  CalibrationPoint,
  CreationContext,
  ElementCreationStep,
  RoomsStep,
  RouteCreationStep,
  SelectionAreasStep,
  StartCreatingElementArgs,
  StartDrawingRoomsArgs,
  StartDrawingRouteArgs,
  StartEditingRoomArgs,
  StartMergingRoomsArgs,
  StartRecalibratingArgs,
  StartSelectionAreasArgs,
  UseInteractionModeResult,
} from "./types";

export const useInteractionMode = (): UseInteractionModeResult => {
  const [interactionMode, dispatch] = useReducer(
    interactionModeReducer,
    INITIAL_INTERACTION_MODE,
  );

  const onResetCallbacksRef = useRef<Set<() => void>>(new Set());

  const isCreatingElement = interactionMode.kind === "creating-element";
  const isDrawingRoute = interactionMode.kind === "drawing-route";
  const isEditingRoute = interactionMode.kind === "editing-route";
  const isEditingPath = interactionMode.kind === "editing-path";
  const isSelectionAreasMode = interactionMode.kind === "selection-areas";
  const isDrawingRooms = interactionMode.kind === "drawing-rooms";
  const isEditingRoom = interactionMode.kind === "editing-room";
  const isRoomsMode = isDrawingRooms || isEditingRoom;
  const isCalibrating = interactionMode.kind === "calibrating";

  const isEditingElement = useMemo(() => {
    if (interactionMode.kind === "editing-element") return true;
    if (
      interactionMode.kind === "creating-element" &&
      interactionMode.step === "adding-elements" &&
      interactionMode.modifier === "editing"
    ) {
      return true;
    }
    return false;
  }, [interactionMode]);

  const isMultiAddModeEnabled = useMemo(() => {
    if (interactionMode.kind !== "creating-element") return false;
    if (interactionMode.step !== "adding-elements") return false;
    return interactionMode.modifier === "multi-add";
  }, [interactionMode]);

  const creationContext = useMemo<CreationContext | null>(() => {
    switch (interactionMode.kind) {
      case "creating-element":
      case "drawing-route":
      case "editing-route":
      case "calibrating": {
        return interactionMode.context;
      }
      default: {
        return null;
      }
    }
  }, [interactionMode]);

  const { isPickingCalibration, calibrationState } = useMemo(() => {
    if (interactionMode.kind !== "calibrating") {
      return {
        isPickingCalibration: false,
        calibrationState: null,
      } as const;
    }
    const { calibration, context } = interactionMode;
    return {
      isPickingCalibration:
        calibration.phase === "picking-anchor" ||
        calibration.phase === "picking-target",
      calibrationState: { state: calibration, pageIndex: context.pageIndex },
    } as const;
  }, [interactionMode]);

  const startCreatingElement = useCallback(
    ({ pageIndex }: StartCreatingElementArgs) => {
      dispatch({ type: "START_CREATING_ELEMENT", pageIndex });
    },
    [],
  );

  const stopCreatingElement = useCallback(() => {
    dispatch({ type: "STOP_CREATING_ELEMENT" });
  }, []);

  const setElementCreationStep = useCallback((step: ElementCreationStep) => {
    dispatch({ type: "SET_ELEMENT_CREATION_STEP", step });
  }, []);

  const setElementCreationGroupName = useCallback((name: string) => {
    dispatch({ type: "SET_ELEMENT_CREATION_GROUP_NAME", name });
  }, []);

  const toggleEditingElement = useCallback(() => {
    dispatch({ type: "TOGGLE_EDITING_ELEMENT" });
  }, []);

  const toggleMultiAdd = useCallback(() => {
    dispatch({ type: "TOGGLE_MULTI_ADD" });
  }, []);

  const startDrawingRoute = useCallback(
    ({ pageIndex }: StartDrawingRouteArgs) => {
      dispatch({ type: "START_DRAWING_ROUTE", pageIndex });
    },
    [],
  );

  const stopDrawingRoute = useCallback(() => {
    dispatch({ type: "STOP_DRAWING_ROUTE" });
  }, []);

  const setRouteCreationStep = useCallback((step: RouteCreationStep) => {
    dispatch({ type: "SET_ROUTE_CREATION_STEP", step });
  }, []);

  const setRouteCreationGroupName = useCallback((name: string) => {
    dispatch({ type: "SET_ROUTE_CREATION_GROUP_NAME", name });
  }, []);

  const startEditingRoute = useCallback(() => {
    dispatch({ type: "START_EDITING_ROUTE" });
  }, []);

  const stopEditingRoute = useCallback(() => {
    dispatch({ type: "STOP_EDITING_ROUTE" });
  }, []);

  const startPathEditing = useCallback(() => {
    dispatch({ type: "START_PATH_EDITING" });
  }, []);

  const stopPathEditing = useCallback(() => {
    dispatch({ type: "STOP_PATH_EDITING" });
  }, []);

  const startSelectionAreas = useCallback(
    ({ pageIndex }: StartSelectionAreasArgs) => {
      dispatch({ type: "START_SELECTION_AREAS", pageIndex });
    },
    [],
  );

  const setSelectionAreasStep = useCallback((step: SelectionAreasStep) => {
    dispatch({ type: "SET_SELECTION_AREAS_STEP", step });
  }, []);

  const stopSelectionAreas = useCallback(() => {
    dispatch({ type: "STOP_SELECTION_AREAS" });
  }, []);

  const startDrawingRooms = useCallback(
    ({ pageIndex }: StartDrawingRoomsArgs) => {
      dispatch({ type: "START_DRAWING_ROOMS", pageIndex });
    },
    [],
  );

  const setRoomsStep = useCallback((step: RoomsStep) => {
    dispatch({ type: "SET_ROOMS_STEP", step });
  }, []);

  const startEditingRoom = useCallback(
    ({ pageIndex, roomId }: StartEditingRoomArgs) => {
      dispatch({ type: "START_EDITING_ROOM", pageIndex, roomId });
    },
    [],
  );

  const stopRooms = useCallback(() => {
    dispatch({ type: "STOP_ROOMS" });
  }, []);

  const startMergingRooms = useCallback(
    ({ pageIndex }: StartMergingRoomsArgs) => {
      dispatch({ type: "START_MERGING_ROOMS", pageIndex });
    },
    [],
  );

  const stopMergingRooms = useCallback(() => {
    dispatch({ type: "STOP_MERGING_ROOMS" });
  }, []);

  const startCalibrating = useCallback(() => {
    dispatch({ type: "START_CALIBRATING" });
  }, []);

  const startRecalibrating = useCallback(
    ({ pageIndex }: StartRecalibratingArgs) => {
      dispatch({ type: "START_RECALIBRATING", pageIndex });
    },
    [],
  );

  const setCalibrationPoint = useCallback((point: CalibrationPoint) => {
    dispatch({ type: "SET_CALIBRATION_POINT", point });
  }, []);

  const stopCalibrating = useCallback(() => {
    dispatch({ type: "STOP_CALIBRATING" });
  }, []);

  const exitInteraction = useCallback(() => {
    dispatch({ type: "EXIT_INTERACTION" });
  }, []);

  const subscribeOnReset = useCallback((callback: () => void) => {
    onResetCallbacksRef.current.add(callback);
    return () => {
      onResetCallbacksRef.current.delete(callback);
    };
  }, []);

  const resetToIdle = useCallback(() => {
    dispatch({ type: "RESET_TO_IDLE" });
    onResetCallbacksRef.current.forEach((callback) => callback());
  }, []);

  return {
    interactionMode,
    isCreatingElement,
    isEditingElement,
    isMultiAddModeEnabled,
    isDrawingRoute,
    isEditingRoute,
    isEditingPath,
    isSelectionAreasMode,
    isDrawingRooms,
    isEditingRoom,
    isRoomsMode,
    isCalibrating,
    isPickingCalibration,
    calibrationState,
    creationContext,
    startCreatingElement,
    stopCreatingElement,
    setElementCreationStep,
    setElementCreationGroupName,
    toggleEditingElement,
    toggleMultiAdd,
    startDrawingRoute,
    stopDrawingRoute,
    setRouteCreationStep,
    setRouteCreationGroupName,
    startEditingRoute,
    stopEditingRoute,
    startPathEditing,
    stopPathEditing,
    startSelectionAreas,
    setSelectionAreasStep,
    stopSelectionAreas,
    startDrawingRooms,
    setRoomsStep,
    startEditingRoom,
    stopRooms,
    startMergingRooms,
    stopMergingRooms,
    startCalibrating,
    startRecalibrating,
    setCalibrationPoint,
    stopCalibrating,
    exitInteraction,
    resetToIdle,
    subscribeOnReset,
  };
};
